/**
 * Dict-Based Workspace State Types
 *
 * These types match the Report Library's /workspaces/{id}/state endpoint response.
 * The workspace state is event-sourced and derived by replaying all events.
 *
 * Key difference from legacy LogicalChainGraph:
 * - nodes/edges/findings are dicts keyed by ID (natural deduplication)
 * - edges use adjacency list format (node_id -> child_ids[])
 *
 * @see 3_code/backend/report_library_service/app/workspace_event_store.py
 */

import type { Cluster } from './cluster';
import type { ModelDefinition } from './modeling';
import type { OnlineSupport } from './online_support';

// =============================================================================
// PRIMITIVE TYPES (match DerivedNode, DerivedFinding, etc.)
// =============================================================================

/**
 * Finding from the workspace event store.
 * Immutable evidence extracted from regulatory documents.
 */
export interface DictFinding {
  finding_id: string;
  source_document_celex?: string;
  source_document_name?: string;
  source_reference?: string;
  source_reference_hierarchy?: string;
  source_text?: string;
  source_snippet?: string;
  extracted_at?: string;
  tool_used?: string;
  document_version?: string;
  // Temporal version pin (BAR-533) — the consolidated version a graph finding
  // was researched against, for version-correct source rendering/links.
  valid_from?: string;
  valid_to?: string;
  version_status?: string;
  // Enriched source metadata for finding card rendering
  source_type?: string;         // "eurlex", "eba_qa", "concept", "pdf", "workspace", "unknown"
  category?: string;            // "regulatory" (default) | "input" — input = workspace-sourced fact
  source_workspace_id?: string; // set when category === "input"
  document_type?: string;       // "Regulation", "Directive", etc.
  document_full_title?: string; // Full official title
  article_title?: string;       // Article title separate from reference
  source_service?: string;      // "graph" or "pdf"
  source_url?: string;          // Direct URL to source
  // PDF positional data (undefined for graph results)
  chunk_id?: string;        // PDF chunk citation marker
  page_number?: number;     // PDF page number (1-indexed)
  paragraph_id?: string;    // Section identifier (e.g., "§4.3.2")
  bbox?: Record<string, any>; // Bounding box for highlight overlay
  // Graph intelligence (cross-references, concepts, Q&As)
  cross_references?: Array<{ direction: string; article: string; celex: string; document_name: string }>;
  concepts?: string[];
  linked_qa?: Array<{ ref: string; target: string; date?: string; title?: string }>;
  qa_topic?: string;                   // EBA Single Rulebook Q&A topic
  qa_legal_act?: string;               // Legal act referenced by the Q&A
  qa_status?: string;                  // Q&A status/publication state
  qa_submitted_date?: string;          // Date the Q&A question was submitted
  qa_clarifies?: string[];             // Articles/provisions clarified by the Q&A
  // Legal classification
  legal_status?: string;              // "binding", "comply_or_explain", "interpretive", "informational"
  // Authority and lifecycle metadata (PDF service)
  authority_abbrev?: string;          // "EBA", "FI", "JC", "CEBS", "EC"
  authority_name_en?: string;         // Full English name of issuing authority
  lifecycle_stage?: string;           // "Final", "Draft", "Consultation"
  reference_type?: string;            // "formal", "synthetic"
  pdf_url?: string;                   // Direct URL to PDF file
  page_url?: string;                  // Web page URL for the document
  // Canonical metadata (PDF service, from #54/#68)
  document_status?: string;           // "Final", "Draft", "Consultation"
  document_category?: string;         // "Final Guideline", "Consultation Paper", etc.
  publication_date?: string;
  regulatory_framework?: string[];    // ["CRR", "CRD", "BRRD"]
  legal_basis?: string[];
  applies_to?: string[];
  effective_date?: string;
  parent_reference?: string;          // Parent document for sub-documents
  replaces?: string;
  amends?: string[];                  // References of instruments this one modifies
  compliance_deadline?: string;
  // Concept-specific structured data
  definition_sources?: Array<{ ref: string; celex: string; short_name: string }>;
  concept_relationships?: Record<string, string[]>;
}

/**
 * Node from the workspace event store.
 * Represents a concept, requirement, or other logical element in the graph.
 */
export interface DictNode {
  node_id: string;
  node_type: string;
  cluster_id: string | null;
  label: string;
  metadata?: Record<string, any>;
  content?: string;
  relevance_note: string;
  parent_ids: string[];
  finding_ids: string[];
  selected_option_id?: string;
  notes?: string;
  materials: Array<{
    material_id: string;
    title: string;
    url?: string;
  }>;
  options: Array<{
    option_id: string;
    label: string;
    description?: string;
  }>;
  created_at?: string;
  updated_at?: string;
  // Plan graph fields (#89)
  workspace_id?: string;
  workspace_type?: string;
  deliverable?: string;
  deliverable_role?: string | null;
  is_terminal?: boolean;
  // Validation fields (#89)
  validation_status?: string;
  workspace_validation_status?: string;
  // True once the child workspace has produced its first deliverable — drives
  // the "In progress" plan-node status (see derivePlanNodeStatus).
  workspace_started?: boolean;
  completion_governance_status?: string;
  completion_governance_reason?: string;
  completion_governance_scope?: string;
  reviews?: Array<Record<string, unknown>>;
}

/**
 * Reference marker linking report text to findings.
 */
export interface DictMarker {
  marker_id: string;
  finding_id: string;
  context?: string;
  label?: string;
  citation_text?: string;
}

/**
 * Report content (draft or final).
 */
export interface DictReport {
  report_id: string;
  content: string;
  title?: string;
}

/**
 * One output-package documentation file (event-sourced, keyed by file_id).
 * Carries its dependency links (the impact-graph edges) + per-document human
 * approval. Approval is cleared whenever the content changes.
 */
export interface DictDocFile {
  file_id: string;
  content: string;
  title?: string;
  template_id?: string;
  doc_kind?: string | null;
  derived_from_model_version?: number | null;
  derived_from_model_hash?: string | null;
  // Dashboard reference (doc_kind "dashboard_html"): the HTML lives in the
  // canonical artifact store, not inline — these point the Results renderer at it.
  derived_from_run_id?: string | null;
  dashboard_artifact_ref?: string | null;
  dashboard_artifact_sha256?: string | null;
  dashboard_artifact_bytes?: number | null;
  approved_by?: string | null;
  approved_at?: string | null;
  approver_role?: string | null;
  finding_refs?: Array<Record<string, unknown>>;
  regulatory_refs?: Array<Record<string, unknown>>;
  approach_choice_ids?: string[];
  classification_refs?: string[];
  policy_ids?: string[];
  source_artifact_refs?: string[];
  model_refs?: string[];
  doc_refs?: string[];
  depends_on_changed_at?: string | null;
  /** Grounding verdict (documentation workspaces): the deterministic
   *  report-grounding pass over this doc's claims against the linked data —
   *  "every regulatory claim carries a resolving [label]{ref:marker_id}". */
  grounding?: DocGrounding | null;
  /** True when the doc changed since `grounding` was computed (re-validate). */
  grounding_stale?: boolean;
}

/** Grounding verdict on an authored document (subset of the backend dict).
 *  Two shapes share this type:
 *   - marker-based report grounding: `{ref:marker_id}` presence — `dangling_markers`.
 *   - documentation TRACEABILITY (`mode: "traceability"`): every regulatory claim
 *     must reference a grounded upstream requirement via `{req:REQ-XXX}` —
 *     `dangling_reqs` (resolve to no requirement) + `ungrounded_reqs` (resolve but
 *     carry no grounding). Both are blocking. */
export interface DocGrounding {
  verdict: 'approved' | 'flagged' | 'rejected' | 'error';
  /** "traceability" for documentation; absent for marker-based grounding. */
  mode?: 'traceability' | string;
  claim_count?: number;
  cited_claim_count?: number;
  uncited_claim_count?: number;
  uncited_claims?: Array<{ text: string; line: number; refs?: string[] }>;
  // Marker-based grounding (research reports).
  dangling_markers?: string[];
  total_markers?: number;
  // Traceability (documentation): `{req:REQ-XXX}` references.
  req_refs?: string[];
  dangling_reqs?: string[];
  ungrounded_reqs?: string[];
  summary?: string;
  /** True iff any uncited claim OR dangling/ungrounded reference — blocks the ship. */
  blocking?: boolean;
  content_hash?: string;
}

/**
 * Output-package metadata (the OutputPackageMeta payload from the backend).
 * Present on an output_package workspace once assembled.
 */
export interface PackagedModelRefState {
  model_id?: string;
  model_version?: number;
  name?: string;
  source_workspace_id?: string;
}

export interface OutputPackageMetaState {
  package_id?: string;
  project_id?: string;
  model_type?: string | null;
  tier?: string | null;
  // One package per project bundles ALL its models.
  models?: PackagedModelRefState[];
  required_file_ids?: string[];
  report_file_ids?: string[];
  provenance?: {
    production_workspace_ids?: string[];
    research_workspace_ids?: string[];
  } | null;
  assembled_at?: string | null;
  assembled_by?: string | null;
  completion_governance_status?: string | null;
  completion_governance_reason?: string | null;
  completion_governance_scope?: string | null;
}

export interface DictUploadedFile {
  upload_id: string;
  workspace_id: string;
  project_id?: string;
  graph_node_id?: string;
  original_filename: string;
  display_name: string;
  mime_type: string;
  byte_size: number;
  sha256?: string;
  upload_status: 'staged' | 'stored' | 'processing' | 'ready' | 'failed' | 'deleted';
  summary?: string | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
  deleted_at?: string;
}

// =============================================================================
// REPORT GROUNDING SEAL (BAR-192 Phase B data → Phase C display)
// =============================================================================

/** Display severity of a grounding finding. Phase C renders only
 *  'suspected' (soft amber); 'confirmed'/'cleared' arrive with Phase D's
 *  semantic adjudication — the renderer switches on this from day one. */
export type GroundingSeverity = 'suspected' | 'confirmed' | 'cleared';

/** BAR-192 Phase E — a persisted human decision over an adjudicated
 *  verdict. Attached by the report-library reducer onto matching seal
 *  findings; identity is (kind, line_sha1, verdict) so decisions follow
 *  the same survival rules as verdicts. */
export interface GroundingReview {
  /** Phase E judgments (accepted/rejected) + Phase F repair values:
   *  accepted_internal (un-sourceable amber, human-judged internal),
   *  fix_requested / source_requested (repair actions in flight).
   *  Auto-repair: good_enough (the nit-judge declared a surviving
   *  confirmed red a refinement nit — system-stamped, stops re-flagging). */
  decision:
    | 'accepted'
    | 'rejected'
    | 'accepted_internal'
    | 'fix_requested'
    | 'source_requested'
    | 'good_enough';
  actor_id: string;
  decided_at: string;
}

/** Auto-repair run summary from workspace /state (BAR-192 auto-repair): the
 *  legible trail "validation ran → fixed M → K nits → L still material". Null
 *  until an auto-repair loop has run on this workspace. */
export interface AutoRepairSummary {
  found: number;
  auto_fixed: number;
  nits: number;
  material_remaining: number;
  rounds: number;
}

/** One grounding finding normalized for display. `line` is 1-indexed
 *  against the raw report markdown and matches the source position
 *  react-markdown exposes on each block node. */
/** Phase D adjudication verdict attached to a finding once the LLM
 *  second reader has judged it inside validate_work. */
export interface GroundingAdjudication {
  verdict: 'supported' | 'overstated' | 'contradicted' | 'unverifiable' | string;
  rationale: string;
  quote: string;
}

export interface GroundingFinding {
  line: number;
  kind: string; // 'uncited_claim' | 'miscited_number' | 'wrong_wire' | ...
  detail: string;
  severity: GroundingSeverity;
  adjudication?: GroundingAdjudication;
  review?: GroundingReview;
  /** Phase E derived display flags: `fixable` on accepted-confirmed
   *  (the Fix button's gate); `dismissed` on rejected-confirmed (amber
   *  with a "dismissed by you" explanation). */
  fixable?: boolean;
  dismissed?: boolean;
  /** Phase F in-flight repair states (decision fix_requested /
   *  source_requested while the finding still exists on the seal). */
  fixPending?: boolean;
  sourcePending?: boolean;
  /** Decision identity (Phase E): sentence fingerprint for claims/sweep,
   *  marker id for dangling refs — what submitGroundingReview keys on. */
  line_sha1?: string;
  marker_id?: string;
}

/** The grounding seal from workspace /state (BAR-192 Phase B): the latest
 *  deterministic grounding verdict pinned to an exact report version via
 *  content hash. `stale: true` means the report changed after this check —
 *  the report is "not validated" and line anchors no longer apply.
 *  Mirrors `grounding_seal.build_seal_payload` + the reducer's stale flag. */
export interface ReportGroundingSeal {
  content_hash: string;
  verdict: 'approved' | 'flagged' | 'rejected';
  stale: boolean;
  gate_blocking?: boolean;
  compartment_id?: string;
  presence?: {
    claim_count?: number;
    cited_claim_count?: number;
    uncited_claim_count?: number;
    uncited_claims?: {
      text: string; line: number;
      severity?: GroundingSeverity; adjudication?: GroundingAdjudication;
      line_sha1?: string; review?: GroundingReview;
    }[];
    dangling_markers?: string[];
  };
  correspondence_counts?: Record<string, number>;
  correspondence_finding_count?: number;
  correspondence_findings?: {
    kind: string; line: number; detail: string;
    severity?: GroundingSeverity; adjudication?: GroundingAdjudication;
    line_sha1?: string; review?: GroundingReview;
  }[];
  /** Phase D: present (non-null) when validate_work ran the adjudicator. */
  adjudication?: {
    ran: boolean;
    /** True when these verdicts were carried across a report edit — only
     *  sentences byte-identical to what the judge saw keep them. */
    carried?: boolean;
    /** How many content-changing edits have happened since the validation
     *  these verdicts come from (carried seals only). */
    edits_since_validation?: number;
    confirmed_count: number;
    cleared_count: number;
    unverifiable_count: number;
    truncated: boolean;
    /** Phase F verify mode: the discovery sweep was skipped — sweep
     *  findings are carried, not re-judged. */
    sweep_skipped?: boolean;
    dangling?: Record<string, GroundingAdjudication & {
      severity?: GroundingSeverity; review?: GroundingReview;
    }>;
    sweep_findings?: GroundingFinding[];
  } | null;
  summary?: string;
  truncated?: boolean;
  trigger?: string;
  /** Which tool's run this seal records for THIS report version:
   *  "validate_work" only when the review approved it, "check_report_grounding"
   *  for the automatic report check. The report-library reducer carries it
   *  across same-version re-seals and drops it when the text changes. */
  validated_by?: 'validate_work' | 'check_report_grounding' | string | null;
  /** The automatic report check rewrote the report on this run. */
  report_changed?: boolean;
  checked_at?: string;
  gate?: {
    mode?: string;
    outcome?: string;
    fix_rounds?: number;
    removed_without_fix?: number;
  } | null;
}

// =============================================================================
// LINKED OUTPUTS (#3 — the "Other workspace" source in the findings panel)
// =============================================================================

/** A single leaf in a linked upstream workspace's OUTPUT — the REFERENCE level
 *  of the Source → Section → Reference tree. Carries a one-line summary BARK
 *  (and the human) sees by default; drill-in opens the upstream workspace. */
export interface LinkedRequirementFindingRef {
  finding_id: string;
  workspace_id?: string;
  /** The inherited first-order regulatory citation (e.g. "CRR Art 92(1)(a)"). */
  citation_excerpt?: string | null;
  /** The backing finding, resolved from the upstream workspace: its document,
   *  reference, and FULL source text — so the panel can drill into the finding
   *  the requirement holds (collapsed by default). */
  source_document_name?: string | null;
  source_reference?: string | null;
  source_text?: string | null;
}

export interface LinkedOutputReference {
  ref_id: string;
  kind: 'report' | 'spec_requirement' | 'delivered_artifact' | 'doc_file' | 'profile' | string;
  title: string;
  /** One-line preview shown in the list (truncated). */
  summary: string;
  /** Full, untruncated content shown in the drill-in detail pane (when present);
   *  falls back to `summary` for kinds whose real content lives in the upstream
   *  workspace (report / doc_file → use "Open workspace"). */
  detail?: string;
  /** spec_requirement only: `regulatory` | `design`. */
  claim_kind?: string | null;
  /** spec_requirement only: the requirement's COMPLETE flat citation set. A
   *  documentation `{req:REQ-XXX}` chip renders the inherited first-order
   *  citation from here (never re-derived). */
  finding_refs?: LinkedRequirementFindingRef[];
  /** spec_requirement only: `grounded` | `grounded_stale` | `ungrounded`. */
  grounding_status?: string | null;
  /** spec_requirement only: the consumers this requirement is routed to. */
  consumers?: string[];
}

/** An upstream workspace feeding into the current one, resolved read-time from
 *  the plan graph (report_library `_build_linked_outputs`). The SECTION level:
 *  one entry per upstream, its references are the upstream's OUTPUT (deliverable)
 *  — never its inner findings/nodes. */
export interface LinkedOutput {
  workspace_id: string;
  node_id: string;
  workspace_name: string;
  workspace_type: string;
  deliverable: string;
  output_spec: string;
  references: LinkedOutputReference[];
  pending: boolean;
}

// =============================================================================
// REGULATORY SCOPE (BAR-505 — the sealed scope a research workspace ran under)
// =============================================================================

/** One typed axis of the regulatory context. `resolved` carries a value;
 *  `not_applicable` / `unresolved` carry a reason instead — never silently
 *  null. Backend owns the schema; keep fields tolerant. */
export interface RegulatoryAxis {
  status: 'resolved' | 'not_applicable' | 'unresolved';
  value?: string | null;
  reason?: string | null;
  confidence?: string | null;
}

/** One per-exposure approach row (all free strings — no enum). Mixed
 *  approaches = many rows; rows of one structure share `structure_id`
 *  (e.g. a securitisation's position + retained risk). */
export interface ScopeApproachRow {
  scope_descriptor: string;
  approach: RegulatoryAxis;
  /** actual | target | comparative | hypothetical — `target` marks the
   *  approach this research explores moving TO (the SA→IRB case). */
  basis: string;
  structure_id?: string | null;
  scope_note?: string | null;
}

/** A typed regulatory context: the shared axes + per-exposure approaches. */
export interface RegulatoryContextData {
  jurisdiction: RegulatoryAxis;
  level_of_application: RegulatoryAxis;
  as_of: RegulatoryAxis;
  approaches: ScopeApproachRow[];
  availability?: string;
}

/** The sealed scope THIS research was conducted under (BAR-505 slice 5):
 *  effective = L1 institution-actual ⊕ L2 query-scope, sealed for
 *  reproducibility. Null / absent on workspaces that never sealed one
 *  (quick lookups seal nothing — absence is the norm, not an error). */
export interface SealedRegulatoryScope {
  /** Schema of the canonical, domain-separated payload covered by the hash. */
  regulatory_scope_input_schema: 'research_regulatory_scope_input/v1';
  /** Lowercase SHA-256 of that canonical payload. */
  regulatory_scope_input_sha256: string;
  effective: RegulatoryContextData;
  l1_snapshot: RegulatoryContextData;
  l2_override: RegulatoryContextData;
  /** inherited (from the linked institution profile) | elicited (standalone) */
  provenance: string;
  seal_id: string | null;
  seal_note: string;
}

// =============================================================================
// WORKSPACE STATE (from /workspaces/{id}/state endpoint)
// =============================================================================

/**
 * Complete workspace state derived from events.
 * This is the single source of truth - read from Report Library.
 */
/** The latest validate_work session, projected by the report library. It is
 *  never replaced by the automatic grounding check, so what validate_work
 *  found stays visible; a later report or graph change marks it stale. */
export interface ReportReview {
  session_id: string;
  reviewer_id?: string | null;
  scope?: string | null;
  compartment_id: string;
  overall_verdict: 'approved' | 'partial' | 'rejected' | 'abandoned' | string;
  summary?: string;
  validation_execution_complete?: boolean | null;
  completed_at?: string;
  counts: { approved: number; flagged: number; rejected: number };
  stale: boolean;
}

export interface DictWorkspaceState {
  workspace_id: string;
  sequence_number: number;
  compartment_id: string;

  // Primitives (keyed by ID for O(1) lookup and natural deduplication)
  findings: Record<string, DictFinding>;
  nodes: Record<string, DictNode>;
  markers: Record<string, DictMarker>;
  reports: Record<string, DictReport>;
  clusters: Record<string, Cluster>;
  models: Record<string, ModelDefinition>;
  // Output-package state (output_package workspaces). doc_files carries the
  // dependency links the impact graph depends on + per-doc approval.
  doc_files: Record<string, DictDocFile>;
  output_package_meta?: OutputPackageMetaState | null;
  online_support: Record<string, OnlineSupport>;
  uploaded_files: Record<string, DictUploadedFile>;

  // Edges as adjacency list: source_node_id -> target_node_ids[]
  edges: Record<string, string[]>;

  // Edge metadata: { from_node_id: { to_node_id: { label, edge_type, output_spec } } }
  edge_metadata?: Record<string, Record<string, { label?: string; edge_type?: string; output_spec?: string }>>;

  // BAR-192: latest grounding seal (verdict pinned to a report version),
  // null when no grounding has ever run on this workspace.
  report_grounding_seal?: ReportGroundingSeal | null;
  report_review?: ReportReview | null;
  /** BAR-192 Phase F (additive): raw decision map keyed
   *  "kind|anchor|verdict" — needed because a repaired finding is gone
   *  from the seal, so the seal alone cannot show "fixed". */
  grounding_reviews?: Record<string, GroundingReview>;
  /** BAR-192 auto-repair (additive): latest auto-repair run summary. */
  auto_repair?: AutoRepairSummary | null;

  // #3: OUTPUTS of upstream workspaces feeding into this one (plan-graph join,
  // derived read-time in report_library). [] when there are no upstreams;
  // optional so existing DictWorkspaceState literals keep compiling.
  linked_outputs?: LinkedOutput[];

  // BAR-505: sealed regulatory scope (research workspaces) — the effective
  // L1⊕L2 context this research is conducted under. Null when never sealed
  // (quick lookups seal nothing). Same forgot-to-surface guard as
  // report_grounding_seal: must be declared here AND mapped in
  // buildDictWorkspaceStateFromResponse or the store silently drops it.
  regulatory_scope?: SealedRegulatoryScope | null;

  // Build-type Project contract declared on the plan workspace. Kept as a
  // generic payload because the backend owns the ProjectStandard schema.
  project?: Record<string, any> | null;

  // Metadata
  derived_at: string;
}

/**
 * API response wrapper for /workspaces/{id}/state
 */
export interface WorkspaceStateResponse {
  success: boolean;
  workspace_id: string;
  sequence_number: number;
  compartment_id: string;
  findings: Record<string, DictFinding>;
  nodes: Record<string, DictNode>;
  edges: Record<string, string[]>;
  edge_metadata?: Record<string, Record<string, { label?: string; edge_type?: string; output_spec?: string }>>;
  markers: Record<string, DictMarker>;
  reports: Record<string, DictReport>;
  clusters?: Record<string, Cluster>;
  models?: Record<string, ModelDefinition>;
  doc_files?: Record<string, DictDocFile>;
  output_package_meta?: OutputPackageMetaState | null;
  online_support: Record<string, OnlineSupport>;
  uploaded_files?: Record<string, DictUploadedFile>;
  // BAR-192: grounding seal — must be declared here or the typed response
  // silently drops the field the backend now sends.
  report_grounding_seal?: ReportGroundingSeal | null;
  report_review?: ReportReview | null;
  // #3 linked-data join — declared so the typed response keeps the field the
  // backend now sends (same forgot-to-surface guard as report_grounding_seal).
  linked_outputs?: LinkedOutput[];
  // BAR-505: sealed regulatory scope — declared so the typed response keeps
  // the field the backend now sends (same forgot-to-surface guard).
  regulatory_scope?: SealedRegulatoryScope | null;
  /** BAR-192 Phase F (additive): raw decision map keyed
   *  "kind|anchor|verdict" — needed because a repaired finding is gone
   *  from the seal, so the seal alone cannot show "fixed". */
  grounding_reviews?: Record<string, GroundingReview>;
  project?: Record<string, any> | null;
  /** BAR-192 auto-repair (additive): latest auto-repair run summary. */
  auto_repair?: AutoRepairSummary | null;
  derived_at: string;

  // Plan workspace state (from event derivation)
  plan_objective?: string | null;
  plan_sections?: Record<string, Record<string, any>>;
  plan_items?: Record<string, Record<string, any>>;
  plan_documents?: Record<string, Record<string, any>>;
  plan_ready?: boolean;

  // Institution context state (from event derivation)
  profile_fields?: Record<string, Record<string, any>>;
  regulatory_choices?: Record<string, Record<string, any>>;
  key_figures?: Record<string, Record<string, any>>;
  open_items?: Record<string, Record<string, any>>;
  institution_context_ready?: boolean;
}

// =============================================================================
// HELPER TYPES
// =============================================================================

/**
 * Workspace format discriminator
 */
export type WorkspaceFormat = 'dict' | 'process' | 'legacy' | 'empty';

/**
 * Empty workspace state for initialization
 */
export const EMPTY_DICT_WORKSPACE: DictWorkspaceState = {
  workspace_id: '',
  sequence_number: 0,
  compartment_id: 'default',
  findings: {},
  nodes: {},
  edges: {},
  markers: {},
  reports: {},
  clusters: {},
  models: {},
  doc_files: {},
  output_package_meta: null,
  online_support: {},
  uploaded_files: {},
  linked_outputs: [],
  regulatory_scope: null,
  project: null,
  derived_at: new Date().toISOString(),
};
